/**
 * Every edit the module editor makes to its draft.
 *
 * Each one hands `updateDraft` a copy to mutate, so the draft is replaced
 * rather than changed in place and React sees a new object.
 *
 * @module components/studio/module-draft-edits
 */

import { DEFAULT_EFFORT_DATA, type EffortData } from "@/components/shared/effort-hours";
import type { Module, ModuleAssessment, ReadingListItem } from "@/types";
import { uid } from "@/utils/uid";

export type UpdateDraft = (update: (draft: Module) => void) => void;

export function moduleDraftEdits(
  updateDraft: UpdateDraft,
  setPloToMimlos: (update: (current: Record<string, string[]>) => Record<string, string[]>) => void,
) {
  return {
    /** @returns The new outcome's id, so the panel can put the keyboard in it. */
    addMimlo: (): string => {
      const id = uid("mimlo");
      updateDraft((draft) => {
        draft.mimlos = [...(draft.mimlos ?? []), { id, text: "" }];
      });
      return id;
    },
    updateMimlo: (mimloId: string, text: string) =>
      updateDraft((draft) => {
        draft.mimlos = (draft.mimlos ?? []).map((mimlo) =>
          mimlo.id === mimloId ? { ...mimlo, text } : mimlo,
        );
      }),
    /** An empty note removes the entry rather than storing a blank reason. */
    setCasDeviationNote: (mimloId: string, note: string) =>
      updateDraft((draft) => {
        const others = (draft.casDeviationNotes ?? []).filter((entry) => entry.mimloId !== mimloId);
        const next = note.trim() ? [...others, { mimloId, note }] : others;
        if (next.length) {
          draft.casDeviationNotes = next;
        } else {
          delete draft.casDeviationNotes;
        }
      }),
    removeMimlo: (mimloId: string) => {
      updateDraft((draft) => {
        draft.mimlos = (draft.mimlos ?? []).filter((mimlo) => mimlo.id !== mimloId);
        const notes = (draft.casDeviationNotes ?? []).filter((entry) => entry.mimloId !== mimloId);
        if (notes.length) {
          draft.casDeviationNotes = notes;
        } else {
          delete draft.casDeviationNotes;
        }
        draft.assessments = (draft.assessments ?? []).map((assessment) => ({
          ...assessment,
          mimloIds: (assessment.mimloIds ?? []).filter((id) => id !== mimloId),
        }));
      });
      setPloToMimlos((current) =>
        Object.fromEntries(
          Object.entries(current).map(([ploId, ids]) => [
            ploId,
            ids.filter((id) => id !== mimloId),
          ]),
        ),
      );
    },
    addAssessment: (): string => {
      const id = uid("asm");
      updateDraft((draft) => {
        draft.assessments = [
          ...(draft.assessments ?? []),
          {
            id,
            title: "",
            type: "Report/Essay",
            weighting: 0,
            mode: "Online",
            outsideTeachingPeriod: false,
            isCapstone: false,
            isHurdle: false,
            mandatoryPass: false,
            integrity: {
              proctored: false,
              viva: false,
              inClass: false,
              originalityCheck: true,
              aiDeclaration: true,
            },
            mimloIds: [],
            notes: "",
          },
        ];
      });
      return id;
    },
    updateAssessment: (assessmentId: string, update: (assessment: ModuleAssessment) => void) =>
      updateDraft((draft) => {
        draft.assessments = (draft.assessments ?? []).map((assessment) => {
          if (assessment.id !== assessmentId) {
            return assessment;
          }
          const copy = { ...assessment };
          update(copy);
          return copy;
        });
      }),
    toggleAssessmentMimlo: (assessmentId: string, mimloId: string, checked: boolean) =>
      updateDraft((draft) => {
        draft.assessments = (draft.assessments ?? []).map((assessment) => {
          if (assessment.id !== assessmentId) {
            return assessment;
          }
          const ids = new Set(assessment.mimloIds ?? []);
          if (checked) {
            ids.add(mimloId);
          } else {
            ids.delete(mimloId);
          }
          return { ...assessment, mimloIds: Array.from(ids) };
        });
      }),
    removeAssessment: (assessmentId: string) =>
      updateDraft((draft) => {
        draft.assessments = (draft.assessments ?? []).filter(
          (assessment) => assessment.id !== assessmentId,
        );
      }),
    addReading: (): string => {
      const id = uid("reading");
      updateDraft((draft) => {
        draft.readingList = [
          ...(draft.readingList ?? []),
          {
            id,
            type: "core",
            author: "",
            title: "",
            publisher: "",
            year: "",
            isbn: "",
            notes: "",
            isCore: true,
          },
        ];
      });
      return id;
    },
    updateReading: (itemId: string, update: (item: ReadingListItem) => void) =>
      updateDraft((draft) => {
        draft.readingList = (draft.readingList ?? []).map((item) => {
          if (item.id !== itemId) {
            return item;
          }
          const copy = { ...item };
          update(copy);
          return copy;
        });
      }),
    removeReading: (itemId: string) =>
      updateDraft((draft) => {
        draft.readingList = (draft.readingList ?? []).filter((item) => item.id !== itemId);
      }),
    updateEffort: (versionKey: string, field: keyof EffortData, value: string | number) =>
      updateDraft((draft) => {
        const effortHours = { ...(draft.effortHours ?? {}) };
        effortHours[versionKey] = {
          ...DEFAULT_EFFORT_DATA,
          ...(effortHours[versionKey] as Partial<EffortData> | undefined),
          [field]: value,
        };
        draft.effortHours = effortHours;
      }),
  };
}

export type ModuleDraftEdits = ReturnType<typeof moduleDraftEdits>;
